import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@themesberg/react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ExcelRenderer } from 'react-excel-renderer';
import * as XLSX from 'xlsx';
import { useFileUpload } from 'react-use-file-upload/dist/lib/useFileUpload';
import { useDispatch } from 'react-redux';
import { updateToastInfo } from '../../../actions/settings';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';

const FileUploadButton = (props) => {
  const {
    buttonText = 'Upload',
    onFileUpload,
    readAllSheets = false,
    setFormInput,
  } = props;
  const { t } = useTranslation();
  const inputRef = useRef();
  const dispatch = useDispatch();

  const { files, setFiles } = useFileUpload();

  function arrayBufferToBase64(buffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  const handleXLSXUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        if (setFormInput) {
          const base64 = arrayBufferToBase64(data);
          const fileName = file.name;
          const contentType = file.type;

          setFormInput((prev) => ({
            ...prev,
            excelFile: {
              base64,
              fileName,
              contentType,
            },
          }));
        }
        const workbook = XLSX.read(data, { type: 'array' });

        const allSheetsData = workbook.SheetNames.map((sheetName) => ({
          name: sheetName,
          data: XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
            header: 1,
            defval: '',
          }),
        }));

        // For CSV files, return the data in the same format as ExcelRenderer
        if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
          const csvData = {
            rows: allSheetsData[0].data,
            cols: allSheetsData[0].data[0] ? allSheetsData[0].data[0].map((_, index) => ({ key: index })) : []
          };
          onFileUpload(csvData);
        } else {
          onFileUpload(allSheetsData);
        }
      } catch (err) {
        console.error(err);
        dispatch(
          updateToastInfo({
            show: true,
            type: 'danger',
            title: 'Upload Failed',
            message: 'Upload & Read File failed. Contact Support',
          })
        );
      }
    };
    reader.readAsArrayBuffer(file);
  };

  useEffect(() => {
    if (files.length > 0) {
      const file = files[files.length - 1];

      if (readAllSheets) {
        handleXLSXUpload(file);
      } else {
        // Check if it's a CSV file
        if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
          // Handle CSV files with XLSX for better parsing
          handleXLSXUpload(file);
        } else {
          ExcelRenderer(file, async (err, resp) => {
            if (err) {
              dispatch(
                updateToastInfo({
                  show: true,
                  type: 'danger',
                  title: 'Upload Failed',
                  message: 'Upload & Read File failed. Contact Support',
                })
              );
            } else {
              onFileUpload(resp);
            }
          });
        }
      }
    }
  }, [files]);

  return (
    <>
      <Button
        onClick={() => inputRef.current.click()}
        variant="primary"
        className="m-1"
      >
        <FontAwesomeIcon icon={faUpload} className="me-2" />
        &nbsp;{t(buttonText)}
      </Button>
      <input
        ref={inputRef}
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        tabIndex={-1}
        type="file"
        className="form-control"
        onChange={(e) => {
          setFiles(e, 'a');
          inputRef.current.value = null;
        }}
        style={{ display: 'none' }}
      />
    </>
  );
};

export default FileUploadButton;
